import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import { NonNullableFormBuilder, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { MatStepper } from "@angular/material/stepper";
import { STEPPER_GLOBAL_OPTIONS } from "@angular/cdk/stepper";
import { MatDialog } from "@angular/material/dialog";
import { BehaviorSubject, Subscription, combineLatest } from "rxjs";
import { distinctUntilChanged, filter, map, take, withLatestFrom } from "rxjs/operators";
import {
  UserAddress,
  UserAddressService
} from "@onsip/common/services/api/resources/userAddress/user-address.service";
import { UserService } from "@onsip/common/services/api/resources/user/user.service";
import {
  GroupAddress,
  GroupAddressService
} from "@onsip/common/services/api/resources/groupAddress/group-address.service";
import {
  VoicemailService,
  AdvVoicemailbox
} from "@onsip/common/services/api/resources/voicemail/voicemail.service";
import { SnackbarService } from "../../../shared/components/snackbar/snackbar.service";
import {
  UserAlias,
  UserAliasService
} from "@onsip/common/services/api/resources/userAlias/user-alias.service";
import {
  AddressSummary,
  AddressSummaryService
} from "@onsip/common/services/api/resources/addressSummary/address-summary.service";
import { AllAddressType } from "@onsip/common/services/api/resources/addressSummary/address-summary";
import { TranslateService } from "@ngx-translate/core";
import { views } from "@onsip/web/app/phone/views";
import { EmailOptions, VoicemailReviewInfo } from "../admin-voicemail-types";
import { ModalMaterialComponent } from "../../../shared/components/modal/modal-material.component";
import { DynamicPricingService } from "@onsip/common/services/dynamic-pricing.service";
import { ExtensionsListComponent } from "@onsip/web/features/shared/components/extensionsList/extensions-list.component";
import {
  convertVmTransferAddress,
  NONE_TRANSFER_CUSTOM_OPTIONS
} from "../config/none-option.helper";
import { RecordingTypesEnum } from "@onsip/web/features/shared/components/togglableRecorder/config/recording-types.enum";
import { createAudioDurationValidator } from "@onsip/web/features/shared/validators/audio-duration.validator";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import { ApiPromiseState } from "@onsip/common/services/api/api-promise-state.service";
import { AddressAutocompleteComponent } from "../../addressAutocomplete/address-autocomplete.component";
import { NAME_VALIDATOR_REGEX } from "@onsip/common/libraries/validators/name-validators";

@Component({
  selector: "onsip-admin-voicemail-setup",
  templateUrl: "./admin-voicemail-setup.component.html",
  styleUrls: ["./admin-voicemail-setup.component.scss"],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false }
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AdminVoicemailSetupComponent implements OnInit, OnDestroy {
  @HostBinding("class.onsip-grid-content")
  @HostBinding("class.onsip-admin-host-grid")
  _dontUse = true;
  @ViewChild("stepper") stepper!: MatStepper;
  @ViewChild("extensionsList") extensionsListRef!: ExtensionsListComponent;
  @ViewChild("address") addressComp!: AddressAutocompleteComponent;
  voicemailFormGroup = this.formbuilder.group({
    mailboxOwner: this.formbuilder.control(""),
    mailboxName: this.formbuilder.control("", [
      // same validations for any name inputs - we can possibly make a common name valiator?
      Validators.maxLength(60),
      Validators.pattern(NAME_VALIDATOR_REGEX)
    ]),
    mailboxNumber: this.formbuilder.control("", [
      Validators.min(1),
      Validators.pattern("^[0-9]*$")
    ]),
    notificationEmail: this.formbuilder.control(""),
    attachAsWav: this.formbuilder.control(false),
    deleteAfterSending: this.formbuilder.control(false),
    mwi: this.formbuilder.control(""),
    timezone: this.formbuilder.control("US/Eastern"),
    sayDateTime: this.formbuilder.control(false),
    sayDuration: this.formbuilder.control(false),
    sayCallerId: this.formbuilder.control(false),
    pagerNumber: this.formbuilder.control("", { validators: Validators.email }),
    extensionsList: this.formbuilder.control(<Array<string>>[]),
    sipAddressUsername: this.formbuilder.control(""),
    transferAddress: this.formbuilder.control(""),
    greeting: this.formbuilder.control(
      <File | Blob | undefined>undefined,
      [],
      [createAudioDurationValidator(this.sanitizer, 60)]
    )
  });

  // copied from voicemail-edit, can be its own component, ¯\_(ツ)_/¯
  timeZones: Array<[string, string]> = [
    // example: [api value, display value]
    ["US/Eastern", "US Eastern"],
    ["US/Central", "US Central"],
    ["US/Mountain", "US Mountain"],
    ["America/Phoenix", "US Mountain - Arizona"],
    ["US/Pacific", "US Pacific"],
    ["US/Hawaii", "US Hawaii"]
  ];

  lessThanMediumBreakpoint = this.breakpointObserver
    .observe([Breakpoints.Small, Breakpoints.XSmall])
    .pipe(map(state => state.matches));
  mobileBreakpoint = this.breakpointObserver
    .observe(Breakpoints.XSmall)
    .pipe(map(state => state.matches));

  views = views;
  E2eLocators = E2eLocators;
  loadingStateComplete$ = new BehaviorSubject(true);

  // domain part of sip address for account
  domain!: string;
  showEditPanel = false;
  displayAudioFile: SafeResourceUrl | undefined;
  greetingType: RecordingTypesEnum = RecordingTypesEnum.RECORD;
  // transferAddress for voicemail
  transferAddress: AddressSummary | undefined;
  /** use to trigger discardGreeting in audio recorder */
  triggerDiscard = false;
  /** dynamic pricing text */
  pricingText = "";
  // api generated addressId for newly submited voicemail - we need this to confirm completion of voicemailAdd
  // so we can perform additional api that requires the voicemail to exist
  newAddressId!: string;
  customOptions = NONE_TRANSFER_CUSTOM_OPTIONS;
  // cache system to help us determine the new address
  private voicemailCache!: Record<string, AdvVoicemailbox>;
  // current voicemail sip address
  private currentSipAddress!: string;

  /** history formGroup - used for reverting back to a previous version of the formGroup
   * a new version is created on each step change
   */
  private historyFormGroup!: VoicemailReviewInfo;
  private historyGreetingType!: RecordingTypesEnum;
  private historyDisplayAudioFile: SafeResourceUrl | undefined;

  /** extracted user alias array from user alias service */
  private listOfAlias: Array<UserAlias> = [];
  /** list of address summary*/
  private allAddressSummaries: Array<AddressSummary> = [];
  private unsubscriber = new Subscription();

  constructor(
    private router: Router,
    private formbuilder: NonNullableFormBuilder,
    private userService: UserService,
    private userAddress: UserAddressService,
    private groupAddress: GroupAddressService,
    private voicemailService: VoicemailService,
    private userAlias: UserAliasService,
    private addressSummaryService: AddressSummaryService,
    private snackbar: SnackbarService,
    private translate: TranslateService,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private cdRef: ChangeDetectorRef,
    private dynamicPricing: DynamicPricingService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit() {
    this.addressSummaryService.addressSummaryBrowse();
    this.setPricing();
    this.unsubscriber.add(
      combineLatest({
        addressSummary: this.addressSummaryService.orgState,
        userAlias: this.userAlias.orgState
      }).subscribe(({ addressSummary, userAlias }) => {
        // we want to get the extension for each address too
        // this will be used to auto populate the mailbox number field if an extension exists
        this.listOfAlias = userAlias;
        this.allAddressSummaries = addressSummary;
        this.domain = addressSummary[0].domain;
      })
    );

    this.unsubscriber.add(
      this.voicemailFormGroup.controls.greeting.statusChanges
        .pipe(
          map(() => this.voicemailFormGroup.controls.greeting.hasError("tooLongDuration")),
          distinctUntilChanged(),
          filter(hasError => !!hasError)
        )
        .subscribe(() => {
          this.snackbar.openSnackBar(
            "To save, make sure the file is less than 1 min long",
            "error"
          );
          this.voicemailFormGroup.controls.greeting.patchValue(undefined);
        })
    );

    this.unsubscriber.add(
      this.voicemailService.orgState.subscribe(voicemails => {
        if (!this.voicemailCache) {
          this.voicemailCache = {};
          voicemails.forEach(voicemail => (this.voicemailCache[voicemail.addressId] = voicemail));
        } else {
          voicemails.forEach(voicemail => {
            if (!this.voicemailCache[voicemail.addressId]) {
              this.voicemailCache[voicemail.addressId] = voicemail;
              this.newAddressId = voicemail.addressId;
            }
          });
        }
      })
    );

    // set checkbox to disable initially until admin has entered a notification email
    this.voicemailFormGroup.controls.attachAsWav.disable();
    this.voicemailFormGroup.controls.deleteAfterSending.disable();

    this.unsubscriber.add(
      this.voicemailFormGroup.controls.notificationEmail.valueChanges.subscribe(value => {
        if (value && this.voicemailFormGroup.controls.notificationEmail.valid) {
          this.voicemailFormGroup.controls.attachAsWav.setValue(true);
          this.voicemailFormGroup.controls.attachAsWav.enable();
        } else {
          this.voicemailFormGroup.controls.attachAsWav.setValue(false);
          this.voicemailFormGroup.controls.attachAsWav.disable();
          this.voicemailFormGroup.controls.deleteAfterSending.setValue(false);
          this.voicemailFormGroup.controls.deleteAfterSending.disable();
        }
      })
    );
    this.unsubscriber.add(
      this.voicemailFormGroup.controls.attachAsWav.valueChanges.subscribe(value => {
        if (value) {
          this.voicemailFormGroup.controls.deleteAfterSending.enable();
        } else {
          this.voicemailFormGroup.controls.deleteAfterSending.setValue(false);
          this.voicemailFormGroup.controls.deleteAfterSending.disable();
        }
      })
    );

    this.unsubscriber.add(
      this.voicemailFormGroup.controls.mailboxOwner.valueChanges
        .pipe(
          distinctUntilChanged(),
          withLatestFrom(this.userAddress.orgState, this.userService.state)
        )
        .subscribe(([owner, userAddress, { state }]) => {
          const address = this.allAddressSummaries.find(address => address.aor === owner);
          const extension = this.listOfAlias.find(
            alias => alias.addressId === address?.addressId
          )?.aliasUsername;

          const userId = userAddress.find(
            userAddress => userAddress.addressId === address?.addressId
          )?.userId as string;

          const email = state[userId]?.contact?.email;

          this.voicemailFormGroup.controls.mailboxName.setValue(address?.name || "");
          this.voicemailFormGroup.controls.mailboxNumber.setValue(
            this.getOnlyNumbers(extension || "")
          );
          this.voicemailFormGroup.controls.notificationEmail.setValue(email || "");

          if (!address || address?.type === "user") {
            this.voicemailFormGroup.controls.mwi.patchValue(address?.aor || "");
            this.addressComp.configureValue();
          }
        })
    );

    this.unsubscriber.add(
      this.voicemailFormGroup.controls.transferAddress.valueChanges.subscribe(transferAddress => {
        this.transferAddress = this.allAddressSummaries.find(
          address => address.aor === transferAddress
        );
      })
    );
    this.initSipAddressSync();
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  setPricing(): void {
    this.dynamicPricing.getDynamicPricing("AdvVoicemailbox", false).then(result => {
      this.pricingText = result;
      this.cdRef.markForCheck();
    });
  }

  handleCancel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: "Cancel setup?",
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(retObj => {
          if (retObj && retObj.doPrimaryAction) {
            this.router.navigate([views.ADMIN_VOICEMAIL]);
          }
        })
    );
  }

  getMwiDisplayName(): string {
    return (
      this.allAddressSummaries.find(
        address => address.aor === this.voicemailFormGroup.controls.mwi.value
      )?.name || "n/a"
    );
  }

  getOwnerDisplayName(): string {
    return (
      this.allAddressSummaries.find(
        address => address.aor === this.voicemailFormGroup.controls.mailboxOwner.value
      )?.name || "n/a"
    );
  }

  getSipAddress() {
    return this.voicemailFormGroup.controls.sipAddressUsername.value && this.domain
      ? this.voicemailFormGroup.controls.sipAddressUsername.value + "@" + this.domain
      : "n/a";
  }

  nextStep(): void {
    if (this.voicemailFormGroup.valid) {
      // complete the current step
      if (this.stepper.selected) this.stepper.selected.completed = true;
      // move to next step
      this.stepper.next();
      // make a copy or there will be pass by reference issues
      // use getRawValue here because we want the disabled formcontrol values too
      // only make a copy if review page does not have a edit panel open.
      // When the edit panel is open, all form fields are treated as a temporary state which can be reverted on the review page
      if (!this.showEditPanel) {
        this.historyFormGroup = { ...this.voicemailFormGroup.getRawValue() };
        this.historyDisplayAudioFile = this.displayAudioFile;
        this.historyGreetingType = this.greetingType;
      }
    } else {
      this.voicemailFormGroup.markAllAsTouched();
      this.voicemailFormGroup.updateValueAndValidity();
    }
  }

  backStep(): void {
    if (this.voicemailFormGroup.valid) {
      // move to previous step only if form is valid
      this.stepper.previous();
      if (this.showEditPanel) {
        this.voicemailFormGroup.setValue(this.historyFormGroup);
        this.greetingType = this.historyGreetingType;
        this.displayAudioFile = this.historyDisplayAudioFile;
        this.showEditPanel = !this.showEditPanel;
      }
    } else {
      this.voicemailFormGroup.markAllAsTouched();
      this.voicemailFormGroup.updateValueAndValidity();
    }
  }

  toggleEditPanel(): void {
    if (!this.showEditPanel) {
      // from view mode to edit mode
      this.showEditPanel = !this.showEditPanel;
    } else {
      // from edit mode to view mode
      this.unsubscriber.add(
        this.dialog
          .open(ModalMaterialComponent, {
            panelClass: ["mat-typography", "onsip-dialog-universal-style"],
            data: {
              title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
              message: this.translate.instant(
                "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
              ),
              primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
              secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
              primaryBtnFlat: true
            }
          })
          .afterClosed()
          .pipe(take(1))
          .subscribe(retObj => {
            if (retObj && retObj.doPrimaryAction) {
              // we want to revert changes if user clicks this
              this.voicemailFormGroup.setValue(this.historyFormGroup);
              this.greetingType = this.historyGreetingType;
              this.displayAudioFile = this.historyDisplayAudioFile;
              this.showEditPanel = !this.showEditPanel;
            }
          })
      );
    }
  }

  submit(): void {
    if (this.voicemailFormGroup.valid) {
      this.loadingStateComplete$.next(false);
      const formGroupValue = this.voicemailFormGroup.value as VoicemailReviewInfo;
      const emailOptions = this.getEmailOptions(formGroupValue);

      const params = {
        Name: formGroupValue.mailboxName,
        Mailbox: formGroupValue.mailboxNumber,
        Email: formGroupValue.notificationEmail,
        "EmailOptions[attach]": emailOptions,
        Mwi: formGroupValue.mwi,
        Pager: formGroupValue.pagerNumber,
        Timezone: formGroupValue.timezone,
        SayCallerId: formGroupValue.sayCallerId,
        SayDateTime: formGroupValue.sayDateTime,
        SayDuration: formGroupValue.sayDuration,
        TransferAddressUsername: convertVmTransferAddress(formGroupValue.transferAddress),
        // Username can be added in many different ways
        // we are going to make the custom sip address take priority because this is specifically inputed by the admin
        // next we will try the assigned owner to the voicemail
        // then if the previous two are not provided, we use the mailbox name which is a required field
        Username:
          formGroupValue.sipAddressUsername ||
          (formGroupValue.mailboxOwner
            ? "vm." + formGroupValue.mailboxOwner.split("@")[0]
            : formGroupValue.mailboxName.toLowerCase().replace(/\s/g, ".")),
        Domain: this.domain
      };

      // we will need this for the optional additional steps
      this.currentSipAddress = params.Username + "@" + params.Domain;

      this.voicemailService.advVoicemailboxAdd(params).then(response => {
        if (response.status === "success") {
          this.nextSteps(formGroupValue);
        } else {
          this.snackbar.openSnackBar(response.data.message, "error");
          this.voicemailService.clearErrors();
        }
        this.loadingStateComplete$.next(true);
      });
    }
  }

  getMailboxNameError(): string {
    const mailboxName = this.voicemailFormGroup.controls.mailboxName;
    return mailboxName.hasError("maxlength")
      ? this.translate.instant("ONSIP_I18N.MUST_BE_LESS_THAN_60_CHARACTERS")
      : mailboxName.hasError("pattern")
      ? this.translate.instant(
          "ONSIP_I18N.MUST_CONTAIN_ONLY_ALPHANUMERIC_CHARACTERS_SPACES_AND_NO_PLUS_COMMA_COLON"
        )
      : mailboxName.hasError("required")
      ? this.translate.instant("ONSIP_I18N.NAME_IS_REQUIRED")
      : "";
  }

  getMailboxNumberError(): string {
    const mailboxNumber = this.voicemailFormGroup.controls.mailboxNumber;
    return mailboxNumber.hasError("min")
      ? "Must be greater than 1"
      : mailboxNumber.hasError("pattern")
      ? "Mailbox can only contain numbers"
      : mailboxNumber.hasError("required")
      ? "Mailbox number is required"
      : "";
  }

  /** next set of optional api steps we can do once we have created the new voicemail box
   * 1. upload a greeting to the voicemailbox
   * 2. set an owner (user or group) for the voicemail
   * 3. set extensions/alias for the voicemail
   * 4. set dids/phone numbers for the voicemail
   */
  private async nextSteps(formGroupValue: VoicemailReviewInfo): Promise<void> {
    const requestsP: Array<ApiPromiseState<GroupAddress | UserAddress>> = [];
    let vmError = false;
    // upload greeting if there is an upload file
    if (this.voicemailFormGroup.value.greeting) {
      const response = await this.voicemailService.advVoicemailboxGreetingUpload(
        "UnavailableGreeting",
        this.voicemailCache[this.newAddressId]?.advVoicemailboxId,
        this.voicemailFormGroup.value.greeting
      );

      if (response.status === "error") {
        this.snackbar.openSnackBar(
          "To save, make sure file is wav format and less than 1 min long",
          "error"
        );
        vmError = true;
      }
    }

    // set owner if requested
    if (formGroupValue.mailboxOwner) {
      const ownerType = this.getOwnerAddressType(formGroupValue.mailboxOwner);
      if (ownerType === "user") {
        requestsP.push(this.editUserAddress(formGroupValue.mailboxOwner, this.currentSipAddress));
      } else if (ownerType === "group") {
        requestsP.push(this.editGroupAddress(formGroupValue.mailboxOwner, this.currentSipAddress));
      }
    }

    // check for any extensions to add
    if (formGroupValue.extensionsList.length > 0) {
      this.extensionsListRef.editExtensions(this.currentSipAddress.split("@")[0]);
    }

    !vmError &&
      Promise.all(requestsP)
        .finally(() => {
          this.clearErrors();
        })
        .then(res => {
          if (res.every(item => item?.status !== "error")) {
            this.handleSuccess();
          } else {
            const error = res.find(item => item?.status === "error" && item.data.message)?.data
              .message as string;
            this.handleError(error);
          }
        })
        .catch(() => {
          this.handleError();
        });
  }

  private handleError(error?: string) {
    error =
      error ||
      (this.translate.instant("ONSIP_I18N.THERE_WAS_AN_ERROR_SAVING_YOUR_INFORMATION") as string);
    this.snackbar.openSnackBar(error, "error");
  }

  private handleSuccess() {
    this.snackbar.openSnackBar(
      this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
      "success"
    );
    this.router.navigate([views.ADMIN_VOICEMAIL]);
  }

  private clearErrors() {
    // clear errors from store
    this.voicemailService.clearErrors();
    this.userAlias.clearErrors();
    this.userAddress.clearErrors();
  }

  private getEmailOptions(formGroupValue: VoicemailReviewInfo): EmailOptions {
    const notifyEmail = formGroupValue.notificationEmail;
    const attachAsWav = formGroupValue.attachAsWav;
    const deleteAfterSending = formGroupValue.deleteAfterSending;

    // if there is no notification email, return no
    if (!notifyEmail) return EmailOptions.NO;
    if (attachAsWav && !deleteAfterSending) return EmailOptions.YES;
    if (attachAsWav && deleteAfterSending) return EmailOptions.YES_THEN_DELETE;

    // we can send an email with no wav file option here
    return EmailOptions.NO;
  }

  private getOwnerAddressType(owner: string): AllAddressType | "" {
    if (owner === "") return "";
    return this.allAddressSummaries.find(address => address.aor === owner)?.addressType || "";
  }

  private editUserAddress(
    voicemailOwner: string,
    DefaultAddress: string
  ): ApiPromiseState<UserAddress> {
    return new Promise((resolve, reject) => {
      this.unsubscriber.add(
        this.userAddress.orgState.pipe(take(1)).subscribe(userAddresses => {
          const newAddress = userAddresses.find(userAddress => userAddress.aor === voicemailOwner);
          if (newAddress) {
            const req = this.userAddress.userAddressEditDefaultAddress({
              Address: newAddress.aor,
              // I don't know why we hard set the timeout to an invalid value so reseting the max number to 120
              // don't want to mess with the types file for now as we use userAddress in a lot of places
              Timeout: newAddress.timeout > 120 ? 120 : newAddress.timeout,
              DefaultAddress
            });
            req?.then(res => resolve(res)).catch(e => reject(e));
          }
        })
      );
    });
  }

  private editGroupAddress(
    voicemailOwner: string,
    DefaultAddress: string
  ): ApiPromiseState<GroupAddress> {
    return new Promise((resolve, reject) => {
      this.unsubscriber.add(
        this.groupAddress.orgState.pipe(take(1)).subscribe(groups => {
          const newAddress = groups.find(
            group => group.username + "@" + group.address.domain === voicemailOwner
          );
          if (newAddress) {
            const req = this.groupAddress.groupAddressEdit({
              Address: newAddress.address.username + "@" + newAddress.address.domain,
              DefaultAddress,
              Ordering: newAddress.ordering,
              Timeout: newAddress.timeout,
              SelectedAddresses: newAddress.groupMemberAddresses.map(
                add => add.address.username + "@" + add.address.domain
              )
            });
            req?.then(res => resolve(res)).catch(e => reject(e));
          }
        })
      );
    });
  }

  private initSipAddressSync(): void {
    this.unsubscriber.add(
      combineLatest({
        mailboxOwner: this.voicemailFormGroup.controls.mailboxOwner.valueChanges,
        mailboxName: this.voicemailFormGroup.controls.mailboxName.valueChanges
      }).subscribe(({ mailboxOwner, mailboxName }) =>
        this.voicemailFormGroup.controls.sipAddressUsername.patchValue(
          mailboxOwner
            ? "vm." + mailboxOwner.split("@")[0]
            : mailboxName.toLowerCase().replace(/\s/g, ".")
        )
      )
    );
  }

  private getOnlyNumbers(str: string) {
    return str?.replace(/\D/g, "");
  }
}
